import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dialog, Transition } from '@headlessui/react';
import { ChevronDownIcon, XIcon } from '@heroicons/react/solid';
import { motion, AnimatePresence } from 'framer-motion';
import React, { Fragment, useState } from 'react';
import { SideBarStates, useSidebarState } from '../../../context/SidebarContext';

const NOOPrevent = (e: any) => {
    e?.preventDefault?.();
};

const initial = { translateX: 0, width: '0%', opacity: 0.5 };
const animate = { translateX: 0, width: '100%', opacity: 1 };
const exit = { translateX: 0, width: '0%', opacity: 0.5 };

export function BasePanelComponent({
    children,
    openState,
}: {
    children: React.ReactNode;
    openState?: SideBarStates;
}) {
    const { state } = useSidebarState();
    return (
        <React.Fragment>
            <AnimatePresence>
                {state === openState && (
                    <motion.div
                        initial={initial}
                        animate={animate}
                        exit={exit}
                        className="fixed top-0 bottom-0 right-0 z-[400] max-w-[100%] md:max-w-[504px] lg:max-w-[640px]"
                    >
                        <motion.div className="relative h-full w-full pr-14">
                            <div className=" h-full w-full">
                                <div className=" w-full h-full ">
                                    <motion.div className="w-full h-full z-30">
                                        <div className="w-full h-full z-30">
                                            <div className="min-h-max h-full flex flex-col bg-white shadow-xl border-l border-gray-300 w-[100%]">
                                                {children}
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </React.Fragment>
    );
}

function ContentlessHeader({ children }: { children: React.ReactNode }) {
    return <div className="flex h-16 items-center justify-between">{children}</div>;
}

function PanelHeader({
    title,
    iconName,
    content,
}: {
    title: string;
    iconName?: IconDefinition;
    content?: React.ReactNode;
}) {
    return (
        <div className="flex h-16 items-center justify-between px-4 py-3 border-b border-gray-300 w-full">
            <div className="flex items-center flex-grow  truncate">
                {!!iconName && (
                    <FontAwesomeIcon icon={iconName} size="lg" className="text-green-400 mr-2" />
                )}
                <h2 className="text-xl font-bold text-gray-900 flex-grow truncate">{title}</h2>
            </div>
            <div className="flex-shrink-0">{content}</div>
        </div>
    );
}

function BorderlessPanelHeader({
    title,
    iconName,
    content,
}: {
    title: string;
    iconName?: IconDefinition;
    content?: React.ReactNode;
}) {
    return (
        <div className="flex h-16 items-center justify-between px-4 py-3 w-full">
            <div className="flex items-center flex-grow  truncate">
                {!!iconName && (
                    <FontAwesomeIcon icon={iconName} size="lg" className="text-green-400 mr-2" />
                )}
                <h2 className="text-xl font-bold text-gray-900 flex-grow truncate">{title}</h2>
            </div>
            <div className="flex-shrink-0">{content}</div>
        </div>
    );
}

function Body({ children, className }: { children: React.ReactNode; className?: string }) {
    return <div className={`relative flex-1  overflow-y-auto ${className}`}>{children}</div>;
}

BasePanelComponent.Header = PanelHeader;
BasePanelComponent.BorderlessHeader = BorderlessPanelHeader;
BasePanelComponent.ContentlessHeader = ContentlessHeader;
BasePanelComponent.Body = Body;

export function Dropdown({ title, emptyText }: { title: string; emptyText: string }) {
    const [open, setOpen] = useState(true);
    return (
        <div className="">
            <button
                className="flex items-center w-full py-4 px-2 bg-teal-050 text-md border-b border-gray-300 focus:outline-none"
                onClick={(e) => {
                    e.preventDefault?.();
                    setOpen((s) => !s);
                }}
            >
                <ChevronDownIcon
                    className={`w-6 h-6 ${
                        open
                            ? 'transform transition-transform duration-200 ease-in-out rotate-0'
                            : 'transform transition-transform duration-200 ease-in-out -rotate-90'
                    }`}
                />

                {title}
            </button>
            {open && (
                <div className="py-4 px-2 flex items-center justify-center">
                    <p className="text-gray-300 text-lg">{emptyText}</p>
                </div>
            )}
        </div>
    );
}
